/**
 * MD-11 AnswerActions — footer cluster Like/Dislike/Copy/Report dưới mỗi câu trả lời.
 *
 * Spec: docs/superpowers/specs/2026-05-11-chat-leftbar-source-upgrade-design.md §3.6
 * DECISIONS: s2026-05-11-CHAT-LEFTBAR-SOURCE-UPGRADE — feedback endpoint correction:
 *   - Use `POST /api/v1/me/feedback` (NOT `/api/v1/feedback` spec wrote)
 *   - Payload: { kind, comment?, citation_cid?, request_id }
 *   - Mapping: Like → kind=helpful, Dislike → kind=wrong, Report → route /report
 *
 * a11y: aria-label tiếng Việt, focus-visible amber outline (CSS).
 * Anti-spam: disable like/dislike sau 1 click 5 phút.
 */

import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';

export type AnswerActionsProps = {
  lang: 'vi' | 'en';
  messageId: string;
  messageContent: string;
  requestId?: string;
  accessToken?: string | null;
};

type Reaction = 'like' | 'dislike' | null;

const iconProps = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function IconThumbUp({ filled }: { filled?: boolean }): ReactElement {
  return (
    <svg
      {...iconProps}
      fill={filled ? 'currentColor' : 'none'}
      aria-hidden="true"
    >
      <path d="M7 22V11M2 13v7a2 2 0 0 0 2 2h3M7 11l4-9a3 3 0 0 1 3 3v5h5a2 2 0 0 1 2 2l-2 7a2 2 0 0 1-2 1H7" />
    </svg>
  );
}

function IconThumbDown({ filled }: { filled?: boolean }): ReactElement {
  return (
    <svg
      {...iconProps}
      fill={filled ? 'currentColor' : 'none'}
      aria-hidden="true"
    >
      <path d="M17 2v11M22 11V4a2 2 0 0 0-2-2h-3M17 13l-4 9a3 3 0 0 1-3-3v-5H5a2 2 0 0 1-2-2l2-7a2 2 0 0 1 2-1h10" />
    </svg>
  );
}

function IconCopy(): ReactElement {
  return (
    <svg {...iconProps} aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function IconFlag(): ReactElement {
  return (
    <svg {...iconProps} aria-hidden="true">
      <path d="M4 22V4a1 1 0 0 1 1-1h12l-3 5 3 5H5" />
    </svg>
  );
}

async function postFeedback(
  kind: 'helpful' | 'wrong' | 'unclear' | 'upl_concern',
  payload: { request_id?: string; citation_cid?: string; comment?: string },
  accessToken?: string | null
): Promise<boolean> {
  try {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    const res = await fetch('/api/v1/me/feedback', {
      method: 'POST',
      headers,
      body: JSON.stringify({ kind, ...payload }),
    });
    return res.ok;
  } catch (e) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[AnswerActions] feedback failed', e);
    }
    return false;
  }
}

export function AnswerActions({
  lang,
  messageId,
  messageContent,
  requestId,
  accessToken,
}: AnswerActionsProps): ReactElement {
  const [reaction, setReaction] = useState<Reaction>(null);
  const [showDislikeForm, setShowDislikeForm] = useState(false);
  const [dislikeComment, setDislikeComment] = useState('');
  const [copyToast, setCopyToast] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    };
  }, []);

  const armReset = useCallback(() => {
    if (resetTimer.current) clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => {
      setReaction(null);
      setSubmitted(false);
    }, 5 * 60 * 1000);
  }, []);

  const handleLike = useCallback(async () => {
    if (submitted) return;
    setReaction('like');
    setSubmitted(true);
    armReset();
    await postFeedback('helpful', { request_id: requestId }, accessToken);
  }, [submitted, armReset, requestId, accessToken]);

  const handleDislikeClick = useCallback(() => {
    if (submitted) return;
    setReaction('dislike');
    setShowDislikeForm(true);
  }, [submitted]);

  const handleDislikeSubmit = useCallback(async () => {
    setSubmitted(true);
    setShowDislikeForm(false);
    armReset();
    await postFeedback(
      'wrong',
      {
        request_id: requestId,
        comment: dislikeComment.slice(0, 200) || undefined,
      },
      accessToken
    );
  }, [armReset, dislikeComment, requestId, accessToken]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(messageContent);
      setCopyToast(true);
      setTimeout(() => setCopyToast(false), 1800);
    } catch (e) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[AnswerActions] clipboard failed', e);
      }
    }
  }, [messageContent]);

  const reportHref = `/report?message_id=${encodeURIComponent(messageId)}`;

  const t = (vi: string, en: string): string => (lang === 'vi' ? vi : en);

  return (
    <div
      className="asklaw-answer-actions"
      role="toolbar"
      aria-label={t('Phản hồi câu trả lời', 'Answer feedback')}
    >
      <button
        type="button"
        className={
          reaction === 'like'
            ? 'asklaw-answer-actions__btn asklaw-answer-actions__btn--active-like'
            : 'asklaw-answer-actions__btn'
        }
        onClick={handleLike}
        disabled={submitted && reaction !== 'like'}
        aria-label={t('Hữu ích', 'Helpful')}
        aria-pressed={reaction === 'like'}
        title={t('Hữu ích', 'Helpful')}
      >
        <IconThumbUp filled={reaction === 'like'} />
      </button>

      <button
        type="button"
        className={
          reaction === 'dislike'
            ? 'asklaw-answer-actions__btn asklaw-answer-actions__btn--active-dislike'
            : 'asklaw-answer-actions__btn'
        }
        onClick={handleDislikeClick}
        disabled={submitted && reaction !== 'dislike'}
        aria-label={t('Chưa chính xác', 'Inaccurate')}
        aria-pressed={reaction === 'dislike'}
        title={t('Chưa chính xác', 'Inaccurate')}
      >
        <IconThumbDown filled={reaction === 'dislike'} />
      </button>

      <button
        type="button"
        className="asklaw-answer-actions__btn"
        onClick={handleCopy}
        aria-label={t('Sao chép câu trả lời', 'Copy answer')}
        title={t('Sao chép', 'Copy')}
      >
        <IconCopy />
      </button>

      <a
        href={reportHref}
        className="asklaw-answer-actions__btn asklaw-answer-actions__btn--link"
        aria-label={t('Báo cáo nội dung', 'Report issue')}
        title={t('Báo cáo', 'Report')}
      >
        <IconFlag />
      </a>

      {copyToast && (
        <span className="asklaw-answer-actions__toast" role="status" aria-live="polite">
          {t('Đã sao chép', 'Copied')}
        </span>
      )}

      {showDislikeForm && (
        <div className="asklaw-answer-actions__dislike-form" role="dialog" aria-modal="false">
          <label className="asklaw-answer-actions__dislike-label" htmlFor={`dislike-${messageId}`}>
            {t('Cho biết lý do (tuỳ chọn)', 'Tell us why (optional)')}
          </label>
          <textarea
            id={`dislike-${messageId}`}
            className="asklaw-answer-actions__dislike-textarea"
            maxLength={200}
            value={dislikeComment}
            onChange={(e) => setDislikeComment(e.target.value)}
            rows={2}
          />
          <div className="asklaw-answer-actions__dislike-buttons">
            <button
              type="button"
              className="asklaw-answer-actions__dislike-cancel"
              onClick={() => {
                setShowDislikeForm(false);
                setReaction(null);
              }}
            >
              {t('Huỷ', 'Cancel')}
            </button>
            <button
              type="button"
              className="asklaw-answer-actions__dislike-submit"
              onClick={handleDislikeSubmit}
            >
              {t('Gửi', 'Submit')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
